import Cookies from "js-cookie";

import { UpdateBrieGPTUserPayload } from "../../components/PageView/PageViewBrieGPT/useUpdateBrieGPTUser";
import { BrieGPTTabs } from "../../components/PageView/PageViewBrieGPT/BrieGPTSidebarElements/Types";
import { GetGPTMessagesProps } from "../../components/PageView/PageViewBrieGPT/useChatMessages";
import { DeleteGPTChatProps } from "../../components/PageView/PageViewBrieGPT/useDeleteGPTChat";
import { GetGPTItemsProps } from "../../components/PageView/PageViewBrieGPT/useBrieGPTItems";
import { BrieTypeProps } from "../../components/PageView/PageViewBrieGPT/BrieGPTSidebar";
import TenantAPI from "./TenantAPI";
import API from "./API";

export default class BrieConversationAPI {
  static async getGPTConversations() {
    return API._bearerRequest(
      "get",
      `/tenant/brie-conversations/gpt`,
      null,
      Cookies.get(`authenticationToken_${TenantAPI.getCurrentTenant()}`)
    );
  }

  static async getGPTConversationByID(conversationID: string) {
    return API._bearerRequest(
      "get",
      `/tenant/brie-conversations/all/${conversationID}`,
      null,
      Cookies.get(`authenticationToken_${TenantAPI.getCurrentTenant()}`)
    );
  }

  static updateGPTConversation({
    conversationID = "new",
    message,
    viewID,
    pageID,
    workspaceID,
  }: {
    conversationID: string;
    message: string;
    viewID?: string;
    pageID?: string;
    workspaceID?: string;
  }) {
    return API._bearerRequest(
      "put",
      `/tenant/brie-conversations/gpt/${conversationID}`, // @ts-ignore Because API is a javascript file.
      {
        payload: {
          message,
          viewID,
          pageID,
          workspaceID,
          url: window.location.href,
        },
      },
      Cookies.get(`authenticationToken_${TenantAPI.getCurrentTenant()}`)
    );
  }

  static deleteGPTConversation(conversationID: string) {
    return API._bearerRequest(
      "delete",
      `/tenant/brie-conversations/all/${conversationID}`, // @ts-ignore Because API is a javascript file.
      null,
      Cookies.get(`authenticationToken_${TenantAPI.getCurrentTenant()}`)
    );
  }

  // Brie GPT (February 2025)
  static getBrieGPTChats(
    props: GetGPTItemsProps &
      (
        | {
            operation: "chats";
          }
        | {
            operation: "projects";
            filterType?: BrieGPTTabs;
          }
      )
  ) {
    return API._bearerRequest(
      "PUT",
      `/tenant/brie-conversations/brieGPT/chats/get-all`,
      props,
      Cookies.get(`authenticationToken_${TenantAPI.getCurrentTenant()}`)
    );
  }

  static getBrieGPTChatMessages(props: GetGPTMessagesProps & { page: number }) {
    return API._bearerRequest(
      "PUT",
      `/tenant/brie-conversations/brieGPT/chats/${props.chatID}/messages/get-all`,
      props,
      Cookies.get(`authenticationToken_${TenantAPI.getCurrentTenant()}`)
    );
  }

  static createBrieGPTChat(
    props:
      | {
          type: "view";
          viewID: string;
          message: string;
          projectID: string | undefined;
        }
      | {
          type: "record";
          recordID: string;
          moduleAPIName: string;
          message: string;
          projectID: string | undefined;
        }
  ) {
    return API._bearerRequest(
      "POST",
      `/tenant/brie-conversations/brieGPT/chat/new`,
      props,
      Cookies.get(`authenticationToken_${TenantAPI.getCurrentTenant()}`)
    );
  }

  static deleteBrieGPTChat(props: DeleteGPTChatProps) {
    return API._bearerRequest(
      "PUT",
      `/tenant/brie-conversations/brieGPT/chat/${props.chatID}/delete-chat`,
      props,
      Cookies.get(`authenticationToken_${TenantAPI.getCurrentTenant()}`)
    );
  }

  static createBrieGPTProject(
    projectName: string,
    brieTypeProps: BrieTypeProps
  ) {
    return API._bearerRequest(
      "POST",
      `/tenant/brie-conversations/brieGPT/project/new`,
      {
        name: projectName,
        ...brieTypeProps,
      },
      Cookies.get(`authenticationToken_${TenantAPI.getCurrentTenant()}`)
    );
  }

  static deleteBrieGPTProject(projectID: string) {
    return API._bearerRequest(
      "DELETE",
      `/tenant/brie-conversations/brieGPT/project/${projectID}/delete`,
      null,
      Cookies.get(`authenticationToken_${TenantAPI.getCurrentTenant()}`)
    );
  }

  static updateBrieGPTProject({
    projectID,
    payload,
  }: {
    projectID: string;
    payload: Record<string, any>;
  }) {
    return API._bearerRequest(
      "PUT",
      `/tenant/brie-conversations/brieGPT/project/${projectID}/update`,
      {
        payload,
      },
      Cookies.get(`authenticationToken_${TenantAPI.getCurrentTenant()}`)
    );
  }

  static updateBrieGPTChat(
    props:
      | {
          type: "view";
          chatID: string;
          viewID: string;
          payload: Record<string, any>;
        }
      | {
          type: "record";
          chatID: string;
          payload: Record<string, any>;
          recordID: string;
        }
  ) {
    return API._bearerRequest(
      "PUT",
      `/tenant/brie-conversations/brieGPT/chat/${props.chatID}/update`,
      props,
      Cookies.get(`authenticationToken_${TenantAPI.getCurrentTenant()}`)
    );
  }

  static getBrieGPTModels() {
    return API._bearerRequest(
      "GET",
      `/tenant/brie-conversations/brieGPT/models`,
      null,
      Cookies.get(`authenticationToken_${TenantAPI.getCurrentTenant()}`)
    );
  }

  static getBrieGPTProject(projectID: string) {
    return API._bearerRequest(
      "GET",
      `/tenant/brie-conversations/brieGPT/project/${projectID}/get`,
      null,
      Cookies.get(`authenticationToken_${TenantAPI.getCurrentTenant()}`)
    );
  }

  static getBrieGPTUser() {
    return API._bearerRequest(
      "GET",
      `/tenant/brie-conversations/brieGPT/users`,
      null,
      Cookies.get(`authenticationToken_${TenantAPI.getCurrentTenant()}`)
    );
  }

  static updateBrieGPTUser(payload: UpdateBrieGPTUserPayload) {
    return API._bearerRequest(
      "PUT",
      `/tenant/brie-conversations/brieGPT/users`,
      payload,
      Cookies.get(`authenticationToken_${TenantAPI.getCurrentTenant()}`)
    );
  }
}
